/**
 * Estimated-position uncertainty — the growing error ellipse.
 *
 * An EP is a best guess, and the longer we run on it the less we should trust
 * it. The error grows with time from a few honest sources: our speed/log isn't
 * exact, the tidal rate is an estimate, and small angle errors in the heading we
 * steer and the set we assume swing the two vectors apart. We model that as an
 * ellipse that grows linearly with the run: speed/rate errors spread it along
 * the track, angle errors spread it across. Defaults are sensible bands, not
 * false precision — the point is to SHOW the doubt, not pretend we've measured it.
 */
import { normaliseBearing, rhumbDestination } from './geodesy';

const D2R = Math.PI / 180;

export interface EpUncertaintyInput {
  hours: number;
  headingDeg: number;
  stwKn: number;
  tidalRateKn: number;
  /** Log / speed-through-water error band, knots. */
  stwErrKn?: number;
  /** Tidal rate error band, knots. */
  rateErrKn?: number;
  /** Steering / heading error band, degrees. */
  headingErrDeg?: number;
  /** Tidal set error band, degrees. */
  setErrDeg?: number;
}

export interface EpUncertainty {
  semiMajorNm: number;
  semiMinorNm: number;
  /** Orientation of the major axis, degrees true. */
  orientationDeg: number;
}

export function epUncertaintyEllipse(i: EpUncertaintyInput): EpUncertainty {
  const stwErr = i.stwErrKn ?? 0.5;
  const rateErr = i.rateErrKn ?? 0.3;
  const hdgErr = i.headingErrDeg ?? 3;
  const setErr = i.setErrDeg ?? 10;
  const h = Math.max(0, i.hours);
  // Along-track: magnitude errors in our speed and the tidal rate accumulate.
  const alongNm = (stwErr + rateErr) * h;
  // Across-track: the heading and set angle errors swing the two vectors sideways.
  const crossNm = (i.stwKn * Math.sin(hdgErr * D2R) + i.tidalRateKn * Math.sin(setErr * D2R)) * h;
  if (alongNm >= crossNm) {
    return { semiMajorNm: alongNm, semiMinorNm: crossNm, orientationDeg: normaliseBearing(i.headingDeg) };
  }
  return { semiMajorNm: crossNm, semiMinorNm: alongNm, orientationDeg: normaliseBearing(i.headingDeg + 90) };
}

/** Ellipse outline ([lon,lat]) centred on `centre`, for drawing the EP error. */
export function ellipsePolygon(
  centre: [number, number],
  u: EpUncertainty,
  steps = 48,
): [number, number][] {
  const pts: [number, number][] = [];
  for (let k = 0; k < steps; k++) {
    const th = (2 * Math.PI * k) / steps;
    const x = u.semiMajorNm * Math.cos(th); // along the major axis
    const y = u.semiMinorNm * Math.sin(th); // along the minor axis
    const dist = Math.hypot(x, y);
    const ang = (Math.atan2(y, x) * 180) / Math.PI; // 0 = along major axis
    pts.push(dist < 1e-9 ? centre : rhumbDestination(centre, normaliseBearing(u.orientationDeg + ang), dist));
  }
  pts.push(pts[0]);
  return pts;
}
